import useWindowSize from '@customHooks/useWindowSize';
import { observer } from 'mobx-react';
import type { ComponentType, ReactElement, ReactNode } from 'react';
import { Suspense, useMemo } from 'react';

interface PageLayoutProps<D, T, M> {
  renderDesktop: ComponentType<D>;
  settingDesktop: D;
  renderLaptop: ComponentType<T>;
  settingLaptop: T;
  renderMobile: ComponentType<M>;
  settingMobile: M;
  widthMobile?: number;
}

function PageLayout<D, T, M>({
  renderDesktop: RenderDesktop,
  renderLaptop: RenderLaptop,
  renderMobile: RenderMobile,
  settingDesktop,
  settingLaptop,
  settingMobile
}): ReactElement<PageLayoutProps<D, T, M>> {
  const { isMobile, isDesktop, isTablet } = useWindowSize();

  const content = useMemo<ReactNode>(() => {
    let content: ReactNode = null;
    if (isDesktop) {
      content = <RenderDesktop {...settingDesktop} isDesktop />;
    }
    if (isTablet) {
      content = <RenderLaptop {...settingLaptop} isTablet />;
    }
    if (isMobile) {
      content = <RenderMobile {...settingMobile} isMobile />;
    }
    return content;
  }, [
    RenderDesktop,
    RenderLaptop,
    RenderMobile,
    isDesktop,
    isMobile,
    isTablet,
    settingDesktop,
    settingLaptop,
    settingMobile
  ]);
  return <Suspense fallback={null}>{content}</Suspense>;
}

export default observer(PageLayout);
